(ns ui.datepicker
  (:require ["@rehookify/datepicker" :as Picker]
            ["date-fns" :refer [format setHours setMinutes parseJSON formatISO]]
            ["lucide-react" :refer [ArrowRight CalendarDays ChevronLeft
                                    ChevronRight]]
            ["react" :as react]
            ["react-day-picker" :refer [DayPicker]]
            [applied-science.js-interop :as jsi]
            [clojure.string :as clojure.string]
            [minorodata.app.utils]
            [ui.button :refer [button]]
            [ui.fields :refer [select input-class]]
            [ui.dropdown-menu]
            [ui.popover :as popover :refer [popover-close popover-content
                                            popover-root popover-trigger]]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]))

(defn- stringify-dates
  [dates]
  (some->> dates
           (mapv #(.getTime %))
           (clojure.string/join ":")))

(defn- format-hour [date]
  (format date "HH:mm"))

(defn- default-dates [value]
  (or (some-> value clj->js) #js []))

(defn- set-time
  [set-selected-dates idx new-value]
  (let [[hours minutes] (mapv #(js/parseInt % 10)
                              (clojure.string/split new-value #":"))
        set-time        (fn [d] (-> d
                                    (setHours hours)
                                    (setMinutes minutes)))]
    (set-selected-dates (fn [dates]
                          (clj->js (map-indexed (fn [i itm] (cond-> itm
                                                              (= i idx) set-time))
                                                dates))))))

(defn- time-picker
  [{:keys [label value disabled time on-change]}]
  [select {:label     label
           :native    false
           :value     value
           :disabled  disabled
           :time      time
           :on-change on-change
           :options   (mapv (fn [hour]
                              {:value    (jsi/get hour :time)
                               :text     (jsi/get hour :time)
                               :disabled (jsi/get hour :disabled)})
                            time)
           :full-width true}])

(defn- time-pickers [{:keys [on-change disabled dates time]}]
  (let [[from to] (or dates [])]
    [:div {:class "mt-4 flex gap-2"}
     [time-picker {:label     "Start time"
                   :value     (or (some-> from format-hour) "00:00")
                   :on-change (partial on-change 0)
                   :disabled  disabled
                   :time      time}]

     [time-picker {:label     "End Time"
                   :value     (or (some-> to format-hour) "23:30")
                   :on-change (partial on-change 1)
                   :disabled  disabled
                   :time      time}]]))

(defn- day-tile [{:keys [disabled props selected day range now]}]
  (let [same-day-selected? (= range "range-start range-end")]
    [:div {:class (clsx {"bg-kleene-neutral-05 dark:bg-kleene-dark-03" (and (not-empty range) (not same-day-selected?))
                         "rounded-l-full" (or (= range "will-be-range-start") (= range "range-start"))
                         "rounded-r-full" (or (= range "will-be-range-end") (= range "range-end"))})}
     [:button (merge
               props
               {:disabled disabled
                :class    (clsx "block w-10 h-10 text-center tabular-nums hover:bg-kleene-secondary-04 transition-colors disabled:pointer-events-none disabled:cursor-default disabled:text-kleene-dark-05 rounded-full"
                                {"ring-kleene-secondary-05 dark:ring-kleene-dark-05 ring-3 ring-px ring-inset" now
                                 "bg-kleene-secondary-04 dark:bg-kleene-dark-04" selected})})
      day]]))

(defn datetime-picker-header [{:keys [component-props month year]}]
  [:header {:class "flex justify-between item-center mb-4"}
   [button (merge (js->clj (jsi/call component-props :subtractOffset #js {:months 1}))
                  {:size    :icon
                   :variant :secondary
                   :icon    ChevronLeft})]

   [:div {:class "flex-1 text-center"}
    [typography/h3 {} month " " year]]

   [button (merge (js->clj (jsi/call component-props :addOffset #js {:months 1}))
                  {:size    :icon
                   :variant :secondary
                   :icon    ChevronRight})]])

(defn- datetime-picker-content
  [{:keys [weekdays calendar time component-props selected-dates on-time-change]}]
  (let [{:keys [year month days]} (jsi/lookup calendar)
        disabled-time             (< (count selected-dates) 2)]
    ^{:key (stringify-dates selected-dates)}
    [:div {:class "inline-flex flex-col"}
     [datetime-picker-header {:component-props component-props
                              :year            year
                              :month           month}]

     [:div
      [:div {:class "grid grid-cols-7 gap-y-px"}
       (for [day weekdays]
         ^{:key day}
         [typography/h4 {:class "text-center text-kleene-dark-05 mb-2"} day])

       (for [d days
             :let [{:keys [$date day now range disabled selected inCurrentMonth]} (jsi/lookup d)]]
         ^{:key (.getTime $date)}
         [:<>
          [day-tile {:props    (js->clj (jsi/call component-props :dayButton d))
                     :disabled (or (not inCurrentMonth) disabled)
                     :range    range
                     :selected selected
                     :now      now
                     :day      day}]])]

      [time-pickers {:dates     selected-dates
                     :disabled  disabled-time
                     :on-change on-time-change
                     :time      time}]]]))

(defn- datetime-picker-compact-input
  "A toolbar-sized trigger: the range as text, no fixed-width fields."
  [{:keys [value]}]
  [:div {:class "inline-flex items-center gap-1.5 h-6 px-2 rounded text-xs whitespace-nowrap cursor-pointer transition-colors hover:bg-kleene-hover-background"}
   [:> CalendarDays {:size 14 :class "text-kleene-text-secondary shrink-0"}]
   (or (some-> value first (format "dd/MM/yy HH:mm")) "Start")
   [:> ArrowRight {:size 12 :class "text-kleene-text-secondary shrink-0"}]
   (or (some-> value second (format "dd/MM/yy HH:mm")) "End")])

(defn datetime-picker-input [{:keys [value]}]
  [:div {:class (clsx (input-class {})
                      "hover:bg-kleene-neutral-05 cursor-pointer transition-colors inline-block w-auto")}
   [:div {:class "flex gap-2"}
    [:div {:class "w-28 inline-block"}
     (or (some-> value first (format "dd/MM/yy HH:mm"))
         [:span {:class "text-kleene-dark-04"} "Start date"])]

    [:> ArrowRight {:size  20
                    :class "text-kleene-dark-05"}]

    [:div {:class "w-28 inline-block"}
     (or (some-> value second (format "dd/MM/yy HH:mm"))
         [:span {:class "text-kleene-dark-04"} "End date"])]

    [:> CalendarDays {:size  20
                      :class "text-kleene-dark-05 ml-4"}]]])

(defn- use-datetime-picker-state [{:keys [value min-date]}]
  (let [now (new js/Date)

        [selected-dates set-selected-dates] (react/useState (default-dates value))

        on-dates-change (fn [[from to]]
                          (let [new-dates (clj->js (cond-> []
                                                     from (concat [from])
                                                     to   (concat [(-> to (setHours 23) (setMinutes 30))])))]
                            (set-selected-dates new-dates)))
        state           (Picker/useDatePicker #js {:selectedDates selected-dates
                                                   :onDatesChange on-dates-change
                                                   :calendar      #js {:startDay       1
                                                                       :mode           "fluid"}
                                                   :dates         #js {:mode           "range"
                                                                       :selectSameDate true
                                                                       :maxDate        now
                                                                       :minDate        min-date}})]


    (react/useEffect
     (fn []
       (when (not= (stringify-dates value) (stringify-dates (js->clj selected-dates)))
         ;; `default-dates`, not `clj->js`: a nil value became nil state, and
         ;; @rehookify/datepicker reads `.length` off `:selectedDates`.
         (set-selected-dates (default-dates value)))
       (fn []))
     #js [(stringify-dates value)])

    {:state              state
     :selected-dates     selected-dates
     :set-selected-dates set-selected-dates}))

(defn- datetime-picker'
  "`presets` is an optional list of `{:label :active? :on-select}` shown as a
   column beside the calendar. Selecting one calls `:on-select` and closes the
   popover, so a preset need not be expressible as two calendar dates."
  [{:keys [label value min-date on-change resetable presets compact?]
    :or {resetable true}}]
  (let [{:keys [state selected-dates set-selected-dates]}
        (use-datetime-picker-state {:value value
                                    :min-date min-date})
        [container set-container] (react/useState nil)
        disabled (< (count selected-dates) 2)

        data            (jsi/get state :data)
        component-props (jsi/get state :propGetters)
        on-time-change  (partial set-time set-selected-dates)]

    [:div {:ref set-container}
     [popover-root
      [popover-trigger {:as-child true}
       [:div {}
        (when label
          [typography/label {:class "block"} label])

        (if compact?
          [datetime-picker-compact-input {:value value}]
          [datetime-picker-input {:value value}])]]

      [popover-content
       {:container container}
       ;; The flex row only appears with presets, so other callers are unaffected.
       (let [calendar [:<>
                       [:f> datetime-picker-content {:data            data
                                                     :component-props component-props
                                                     :selected-dates  selected-dates
                                                     :on-time-change  on-time-change
                                                     :calendar        (first (jsi/get data :calendars))
                                                     :time            (jsi/get data :time)
                                                     :weekdays        (jsi/get data :weekDays)}]

                       [:footer {:class "mt-4 flex justify-end gap-2"}
                        (when resetable
                          [popover-close
                           [:div
                            [button {:variant  :secondary
                                     :on-click #(on-change [])}
                             "Reset"]]])
                        [popover-close {:class (when disabled "pointer-events-none")}
                         [:div
                          [button {:disabled disabled
                                   :on-click #(on-change (js->clj selected-dates))}
                           "Apply"]]]]]]
         (if (seq presets)
           [:div {:class "flex gap-4"}
            [:div {:class "flex flex-col gap-0.5 pr-4 border-r border-kleene-neutral-05 dark:border-kleene-dark-03 min-w-[9rem]"}
             (for [{:keys [label active? on-select]} presets]
               ^{:key label}
               [popover-close
                [:div
                 [button {:variant    (if active? :empty :empty-transparent)
                          :size       :small
                          :full-width true
                          :content-class "justify-start"
                          :on-click   on-select}
                  label]]])]
            [:div calendar]]
           calendar))]]]))

(defn datetime-picker
  [props]
  [:f> datetime-picker' props])

(defn try-formatting-date [date]
  (try
    (format (parseJSON date) "dd/MM/yy")
    (catch js/Error e
     ; (js/console.error "Failed to parse date" date)
      nil)))

(defn date-picker' [{:keys [field min-date max-date label
                            class full-width required]
                     :or   {full-width true}}]

  (let [value (if (clojure.string/blank? (:value field)) nil (:value field))

        [date setDate] (react/useState (when value (parseJSON value)))
        [open setOpen] (react/useState false)]

    (react/useEffect
     (fn []
       (when value (setDate (parseJSON value)))
       (fn []))
     #js [value])

    [ui.popover/popover {:trigger-as-child true
                         :props {:hide-arrow true
                                 :modal true
                                 :sideOffset 10
                                 :align "start"
                                 :class (clsx "z-2500 items-stretch bg-white rounded-md p-px text-base shadow-lg ring-inset ring-1 overflow-hidden ring-kleene-neutral-05 focus:outline-hidden w-full"
                                              "pointer-events-none!"
                                              "dark:bg-kleene-dark-02 dark:ring-kleene-dark-03")
                                 :style {:min-width "var(--radix-dropdown-menu-trigger-width)"
                                         :max-height  "min(var(--radix-dropdown-menu-content-available-height), 400px)"}}

                         :open open
                         :on-open-change (fn [o]
                                           (setOpen o))
                         :content-props {:on-open-auto-focus (fn [e] (.preventDefault e))
                                         :class (clsx "z-2500 items-stretch bg-white rounded-md p-px text-base shadow-lg ring-inset ring-1 overflow-hidden ring-kleene-neutral-05 focus:outline-hidden w-full"
                                                      "dark:bg-kleene-dark-02 dark:ring-kleene-dark-03"
                                                      "pointer-events-auto!")
                                         :hide-arrow true
                                         :sideOffset 0}

                         :content [:div {}
                                   [:> DayPicker {:mode "single"
                                                  :selected (or date js/undefined)
                                                  :defaultMonth (or date (js/Date.))
                                                  :onSelect (fn [date]
                                                              (let [formatted-date (jsi/call date :toISOString)]
                                                                ((:action field) formatted-date)
                                                                (setOpen false)))

                                                  :startMonth (when min-date (parseJSON min-date))
                                                  :endMonth   (when max-date (parseJSON max-date))
                                                  :hidden     [(when min-date {:before (parseJSON min-date)})
                                                               (when max-date {:after (parseJSON max-date)})]

                                                  :class      "p-3"
                                                  :classNames {:months "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0"
                                                               :month "space-y-4"
                                                               :caption "flex justify-center pt-1 relative items-center px-2"
                                                               :caption_label "font-medium grow"
                                                               :nav "absolute right-2 top-2 space-x-1 flex flex-row items-center"
                                                               :button_previous "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
                                                               :button_next "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
                                                               :chevron "fill-kleene-dark-05"
                                                               :month_grid "w-full border-collapse space-y-1"
                                                               :weekdays "flex"
                                                               :weekday "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]"
                                                               :week "flex w-full mt-2"
                                                               :selected "bg-kleene-primary-01 dark:text-kleene-text-darkest hover:bg-kleene-selected-background hover:text-kleene-text dark:hover:text-kleene-text focus:bg-kleene-selected-background focus:text-kleene-text rounded-full"
                                                               :day (clsx "h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20")
                                                               :day_button "h-9 w-9 p-0 font-normal hover:rounded-full hover:bg-kleene-selected-background"
                                                               :range_end "day-range-end"
                                                               :today "bg-accent text-accent-foreground"
                                                               :outside  "day-outside text-muted-foreground opacity-50"
                                                               :disabled "text-muted-foreground opacity-50"
                                                               :range_middle "bg-accent text-accent-foreground"
                                                               :hidden "invisible"}}]]}
     [:div {:class (clsx "cursor-pointer" {"w-full" full-width})}
      [ui.fields/input {:value (or (try-formatting-date value) "")
                        :class "cursor-pointer! pointer-events-none"
                        :label label
                        :disabled false
                        :placeholder "Select date"
                        :required required
                        :full-width full-width
                        :icon  CalendarDays}]]]))


(defn date-picker
  [props]
  [:f> date-picker' props])
